import React from "react";
import Slider from "react-slick";
import Banner from "./Banner";
import type { IBannerModuleFields, IBanner } from "@types-contentful";
import styled from "styled-components";
import { colours } from "@helpersindex";
import Icon from "@components/Icon";

interface BannerCarouselProps extends IBannerModuleFields {
	id?: string;
}

const Container = styled.div`
  position: relative;

  /* Override react-slick styles to ensure consistent height */
  .slick-track {
    display: flex !important;
    align-items: stretch !important;
  }

  .slick-slide {
    height: auto !important;
    display: flex !important;
    align-items: stretch !important;
  }

  .slick-slide > div {
    height: 100% !important;
    display: flex !important;
    align-items: stretch !important;
  }

  /* Ensure the banner inside each slide takes full height */
  .slick-slide > div > div {
    height: 100% !important;
    display: flex !important;
    align-items: stretch !important;
  }
`;

const ControlsBar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: -40px;
  width: 100%;
  display: flex;
  justify-content: center !important;
  align-items: center !important;
  gap: 12px;
  background: ${colours.PEACH};
  height: 48px;
  z-index: 10;
`;

const ArrowButton = styled.button<{ disabled?: boolean }>`
 width: 44px !important;  /* Increased from 32px */
  height: 44px !important; /* Increased from 32px */
  min-width: 44px !important;
  min-height: 44px !important;

  display: block !important;
  align-items: center;
  justify-content: center;
  background: transparent;
  /* border-radius: 50%; */
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  border: none;
  outline: none;
  color: white;
  z-index: 11;
  padding: 0 !important;
  margin: 0 !important;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition: opacity 0.2s ease;
  
  &:focus {
    outline: none;
  }
  &:hover {
    border: none !important;
    background: transparent !important;
  }
`;

const DotsWrapper = styled.ul`
  display: flex !important;
  justify-content: center;
  align-items: center;
  margin: 0 !important;
  padding: 0;
  background: transparent;
  list-style: none;
  z-index: 10;
`;

const Dot = styled.li<{ active?: boolean }>`
  width: ${({ active }) => (active ? "56px" : "24px")};
  height: 4px;
  margin: 0 2px;
  background-color: ${({ active }) => (active ? colours.WHITE : colours.PALE_PEACH)};
  border-radius: 12px;
  transition: width 0.3s ease-in-out, background-color 0.3s;
  cursor: pointer;
`;

const CarouselContainer = styled.div``;

const iconSizes = {
	iconMobile: 32,
	iconTablet: 32,
	iconLaptop: 32,
	iconDesktop: 32,
	iconDesktopLarge: 32,
};

const BannerCarousel: React.FC<BannerCarouselProps> = ({
	banners,
	autoplay,
	loop,
	id,
}) => {
	if (!banners || banners.length < 2 || banners.length > 4) {
		return null;
	}

	const settings = {
		dots: true,
		infinite: loop,
		speed: 500,
		slidesToShow: 1,
		slidesToScroll: 1,
		autoplay: autoplay,
		autoplaySpeed: 4000,
		arrows: false, // We'll render arrows manually
		appendDots: (dots: React.ReactNode) => <></>, // We'll render dots manually
		customPaging: () => <></>, // Not used
	};

	// We'll need to control the slider to move with our custom arrows
	const sliderRef = React.useRef<any>(null);
	const [current, setCurrent] = React.useState(0);

	const handlePrev = () => {
		console.log("handlePrev called");
		if (sliderRef.current) {
			console.log("sliderRef exists, calling slickPrev");
			if (loop) {
				// If loop is enabled, wrap around to the last slide
				const newIndex = current === 0 ? banners.length - 1 : current - 1;
				sliderRef.current.slickGoTo(newIndex);
			} else {
				// If loop is disabled, only go to previous if not at the first slide
				if (current > 0) {
					sliderRef.current.slickGoTo(current - 1);
				}
			}
		} else {
			console.log("sliderRef is null");
		}
	};

	const handleTouchStart = (handler: () => void) => (e: React.TouchEvent) => {
		e.preventDefault();
		handler();
	};
	const handleNext = () => {
		console.log("handleNext called");
		if (sliderRef.current) {
			console.log("sliderRef exists, calling slickNext");
			if (loop) {
				// If loop is enabled, wrap around to the first slide
				const newIndex = current === banners.length - 1 ? 0 : current + 1;
				sliderRef.current.slickGoTo(newIndex);
			} else {
				// If loop is disabled, only go to next if not at the last slide
				if (current < banners.length - 1) {
					sliderRef.current.slickGoTo(current + 1);
				}
			}
		} else {
			console.log("sliderRef is null");
		}
	};
	const handleDotClick = (index: number) => {
		if (sliderRef.current) {
			sliderRef.current.slickGoTo(index);
		}
	};

	return (
		<Container
			data-testid={`banner-carousel-${id || ""}`
				.toLowerCase()
				.replace(/\s+/g, "-")}
		>
			<CarouselContainer>
				<Slider
					{...settings}
					ref={sliderRef}
					beforeChange={(_, next) => setCurrent(next)}
				>
					{banners.map((banner: IBanner, idx: number) => (
						<div key={banner.sys.id || idx}>
							<Banner
								{...(banner.fields as any)}
								id={banner.sys.id}
								carouselIndex={idx}
							/>
						</div>
					))}
				</Slider>
				<ControlsBar data-testid="control-bar">
					<ArrowButton
						onClick={handlePrev}
						onTouchStart={handleTouchStart(handlePrev)}
						aria-label="Previous banner"
						type="button"
						disabled={!loop && current === 0}
					>
						<Icon
							iconName="chevron-left-white"
							iconSizes={iconSizes}
							alt="Previous banner"
							style={{
								pointerEvents:
									"none" /* Prevent icon from intercepting events */,
								display: "block",
							}}
						/>
					</ArrowButton>
					<DotsWrapper>
						{banners.map((_, idx) => (
							<Dot
								key={idx}
								active={idx === current}
								onClick={() => handleDotClick(idx)}
								role="button"
								aria-label={`Go to slide ${idx + 1}`}
							/>
						))}
					</DotsWrapper>
					<ArrowButton
						onClick={handleNext}
						onTouchStart={handleTouchStart(handleNext)}
						aria-label="Next banner"
						type="button"
						disabled={!loop && current === banners.length - 1}
					>
						<Icon
							iconName="chevron-right-white"
							iconSizes={iconSizes}
							alt="Next banner"
							style={{
								pointerEvents:
									"none" /* Prevent icon from intercepting events */,
								display: "block",
							}}
						/>
					</ArrowButton>
				</ControlsBar>
			</CarouselContainer>
		</Container>
	);
};

export default BannerCarousel;
